/**
 * A property image reference as a URL/key string or media metadata object.
 */
export type ImageRefInput =
  | string
  | { file_key?: string | null; url?: string | null }
  | null
  | undefined;

/**
 * Picks the first usable ref (url, then file_key) off a media-style image object.
 */
function refFromMediaObject(item: { file_key?: string | null; url?: string | null }): string {
  const raw = [item.url, item.file_key].find(Boolean) ?? '';
  return String(raw).trim();
}

/**
 * Coerces a single image input into a trimmed non-empty ref, or empty string.
 */
function coerceImageRef(item: ImageRefInput): string {
  if (typeof item === 'string') return item.trim();
  if (!item || typeof item !== 'object') return '';
  return refFromMediaObject(item);
}

/**
 * Normalizes caller-supplied image lists (URL strings or media objects) into
 * non-empty string refs used for Instagram publish gating.
 */
export function collectImageRefs(images?: ImageRefInput[] | null): string[] {
  if (!Array.isArray(images)) return [];
  return images.map(coerceImageRef).filter(Boolean);
}

/**
 * Strict image presence check for Instagram publish gating.
 * Undefined, empty, or blank-only arrays mean no images.
 */
export function resolveHasImages(propertyImages?: ImageRefInput[] | null): boolean {
  return collectImageRefs(propertyImages).length > 0;
}

/**
 * Instagram gate that matches the gallery source of truth: S3/view URLs and/or
 * property media keys. Empty media metadata alone must not block when fetched
 * gallery URLs exist (common when images live in storage but media.images is empty).
 */
export function resolvePublishHasImages(
  propertyImages?: ImageRefInput[] | null,
  fetchedImageUrls?: ImageRefInput[] | null,
): boolean {
  return resolveHasImages(fetchedImageUrls) || resolveHasImages(propertyImages);
}

export interface QueuedHintRow {
  status: string;
  post_id?: string | null;
  listing_id?: string | null;
}

/**
 * Whether a publication row should show the queued/scheduled processing hint.
 */
export function showQueuedProcessingHint(row: QueuedHintRow): boolean {
  if (row.post_id || row.listing_id) return false;
  return row.status === 'queued' || row.status === 'scheduled';
}

/**
 * Drops Instagram from the selection when the property has no images.
 */
export function deselectInstagramWithoutImages(
  selected: string[],
  hasImages: boolean,
): string[] {
  if (hasImages) return selected;
  return selected.filter((id) => id !== 'instagram');
}

/**
 * Converts a datetime-local value into an ISO string using local timezone.
 */
function toScheduledAtIso(localValue: string): string {
  const [datePart, timePart] = localValue.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = (timePart || '00:00').split(':').map(Number);
  return new Date(year, month - 1, day, hour, minute).toISOString();
}

/**
 * Builds the scheduled_at ISO field for the publish payload, or undefined.
 */
export function buildScheduledAtPayloadField(
  scheduleEnabled: boolean,
  scheduledAt: string,
): string | undefined {
  if (!scheduleEnabled || !scheduledAt) return undefined;
  return toScheduledAtIso(scheduledAt);
}

const ERROR_STATUSES = new Set(['error', 'failed', 'publish_failed']);

/**
 * Whether a publication status should offer retry.
 */
export function isErrorStatus(status: string): boolean {
  return ERROR_STATUSES.has(status);
}

export interface BoostEligibility {
  portal: string;
  postId?: string | null;
  adId?: string | null;
  isConnected: boolean;
  fbHasPaymentMethod: boolean;
  /**
   * ISO-8601 expiry timestamp for this row's own network connection token,
   * if known. Omitted/unparseable fails open (does not block boosting) --
   * only a timestamp that has definitively already passed blocks it, so a
   * connection whose expiry just isn't tracked never gets silently hidden.
   */
  tokenExpiresAt?: string | null;
}

/** Whether a token expiry timestamp has definitively already passed. */
function isTokenExpired(expiresAt?: string | null): boolean {
  if (!expiresAt) return false;
  const expiry = new Date(expiresAt).getTime();
  if (Number.isNaN(expiry)) return false;
  return expiry < Date.now();
}

/**
 * Whether a Facebook or Instagram publication row can be boosted (Impulsionar).
 *
 * Instagram ads bill through the same Facebook ad account as Facebook ads
 * (Meta's Marketing API has no separate Instagram ad account), so eligibility
 * for both portals is driven by the one shared fbHasPaymentMethod flag.
 */
export function canBoostPublication(row: BoostEligibility): boolean {
  if (row.portal !== 'facebook' && row.portal !== 'instagram') return false;
  if (!row.postId || row.adId || !row.isConnected) return false;
  if (isTokenExpired(row.tokenExpiresAt)) return false;
  return row.fbHasPaymentMethod;
}

/**
 * Whether Facebook Marketplace should appear in the publish portal list.
 */
export function includesMarketplacePortal(marketplaceFlagOn: boolean): boolean {
  return marketplaceFlagOn;
}

const PENDING_PUBLICATION_STATUSES = new Set([
  'queued',
  'scheduled',
  'error',
  'failed',
  'publish_failed',
]);

/**
 * Statuses that should still render a portal row before external ids exist
 * (async publish in flight, or a failed attempt with nothing to link).
 */
export function isPendingPublicationStatus(status: string): boolean {
  return PENDING_PUBLICATION_STATUSES.has(status);
}

/**
 * Whether a portal row should show the failed/retry status after a publish attempt.
 */
export function hasPublishError(
  currentResult: { status?: string } | undefined,
  savedPortalStatus: string | undefined,
): boolean {
  const status = currentResult?.status;
  if (status === 'error' || status === 'failed') return true;
  return savedPortalStatus === 'error';
}

/**
 * Instagram appears in the publish dialog when an Instagram-capable feature is on
 * and Instagram itself is actually enabled — via a connected Facebook Page or via
 * the native Instagram connection. Row stays locked until IG is linked either way.
 */
export function shouldShowInstagramPortal(
  instagramFeatureOn: boolean,
  enabledPortals: string[],
): boolean {
  return instagramFeatureOn && enabledPortals.includes('instagram');
}

/**
 * Whether the Instagram checkbox must stay disabled (no images and/or no linked IG).
 */
export function isInstagramOptionLocked(
  hasImages: boolean,
  igAccountId?: string | null,
): boolean {
  return !hasImages || !igAccountId;
}

/**
 * Drops facebook/instagram from enabled portals when neither connection that can
 * carry them is present, so the publish dialog can show the connect-portals empty
 * state. Instagram survives on `igConnected` alone (the native Instagram Business
 * Login connection has no Facebook Page involved), independently of `fbConnected`.
 */
export function effectiveEnabledPortals(
  enabledPortals: string[],
  fbConnected: boolean,
  igConnected = false,
): string[] {
  return enabledPortals.filter((p) => {
    if (p === 'facebook') return fbConnected;
    if (p === 'instagram') return fbConnected || igConnected;
    return true;
  });
}

/**
 * Portals the publish dialog may list: enabled, Meta-gated, and actually connected.
 * Empty result drives the connect-portals empty state with settings redirect.
 * When `connectedPortals` is null (field absent), keep the Meta-gated enabled list.
 */
export function portalsAvailableToPublish(
  enabledPortals: string[],
  connectedPortals: string[] | null,
  fbConnected: boolean,
  igConnected = false,
): string[] {
  const enabled = effectiveEnabledPortals(enabledPortals, fbConnected, igConnected);
  if (connectedPortals === null) return enabled;
  return enabled.filter((id) => connectedPortals.includes(id));
}
